import { Fragment, type ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { useSampleDetailActionLabels } from '../../hooks/useSampleDetailActionLabels';
import type { Sample } from '../../services/sample.service';
import TextContentSep from '../common/TextContentSep';

export type SampleDetailModalActionsProps = {
  sample: Sample;
  canManage: boolean;
  canAddUsage: boolean;
  readOnlyTitle: string;
  onAddUsage: () => void;
  onShare?: () => void;
  onEdit?: (sample: Sample) => void;
  onDelete?: (sample: Sample) => void;
  onOpenFullPage: () => void;
};

/**
 * 詳細モーダル操作帯。狭幅はフルページヒーロー同様（使用＝ラベル、他＝アイコン）。
 * ボタン間のみ TextContentSep（先頭に置かない）。
 */
export default function SampleDetailModalActions({
  sample,
  canManage,
  canAddUsage,
  readOnlyTitle,
  onAddUsage,
  onShare,
  onEdit,
  onDelete,
  onOpenFullPage,
}: SampleDetailModalActionsProps) {
  const {
    usageLabel,
    usageShort,
    editLabel,
    deleteLabel,
    editTitle,
    deleteTitle,
    shareShort,
    shareTitle,
    pageLabel,
    pageShort,
  } = useSampleDetailActionLabels();

  const actions: Array<{ key: string; node: ReactNode }> = [];
  if (canManage && canAddUsage) {
    actions.push({
      key: 'usage',
      node: (
        <Button
          variant="primary"
          size="sm"
          className="sample-detail-modal-action sample-detail-modal-action--primary"
          onClick={onAddUsage}
          title={usageLabel}
          aria-label={usageShort}
        >
          <i className="fas fa-notes-medical" aria-hidden="true" />
          <span className="sample-detail-modal-action-label">{usageShort}</span>
        </Button>
      ),
    });
  }
  if (onEdit && canManage) {
    actions.push({
      key: 'edit',
      node: (
        <Button
          variant="outline-warning"
          size="sm"
          className="sample-detail-modal-action sample-detail-modal-action--icon"
          onClick={() => onEdit(sample)}
          title={editTitle}
          aria-label={editLabel}
        >
          <i className="fas fa-pen" aria-hidden="true" />
          <span className="sample-detail-modal-action-label">{editLabel}</span>
        </Button>
      ),
    });
  }
  if (onDelete && canManage) {
    actions.push({
      key: 'delete',
      node: (
        <Button
          variant="outline-danger"
          size="sm"
          className="sample-detail-modal-action sample-detail-modal-action--icon"
          onClick={() => onDelete(sample)}
          title={deleteTitle}
          aria-label={deleteLabel}
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
          <span className="sample-detail-modal-action-label">{deleteLabel}</span>
        </Button>
      ),
    });
  }
  if (onShare) {
    actions.push({
      key: 'share',
      node: (
        <Button
          variant="outline-primary"
          size="sm"
          className="sample-detail-modal-action sample-detail-modal-action--icon"
          onClick={onShare}
          disabled={!canManage}
          title={!canManage ? readOnlyTitle : shareTitle}
          aria-label={shareShort}
        >
          <i className="fas fa-share-alt" aria-hidden="true" />
          <span className="sample-detail-modal-action-label">{shareShort}</span>
        </Button>
      ),
    });
  }
  actions.push({
    key: 'page',
    node: (
      <Button
        variant="outline-secondary"
        size="sm"
        className="sample-detail-modal-action sample-detail-modal-action--icon sample-detail-modal-action--page"
        onClick={onOpenFullPage}
        title={pageLabel}
        aria-label={pageShort}
      >
        <i className="fas fa-file-alt" aria-hidden="true" />
        <span className="sample-detail-modal-action-label">{pageShort}</span>
      </Button>
    ),
  });

  return (
    <div className="sample-detail-modal-actions mb-2">
      {actions.map((action, index) => (
        <Fragment key={action.key}>
          {index > 0 ? <TextContentSep /> : null}
          {action.node}
        </Fragment>
      ))}
    </div>
  );
}
